import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { AlertCircle, MapPin, X } from 'lucide-react';

// Hide site bar on login/registration entry screens
const HIDE_ON_PATHS = ['/', '/login', '/patient-details', '/registration-with-adhar', '/registration-with-adhar-otp-verify', '/capture-patient-image'];

const PatientSiteSelector = () => {
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [branches, setBranches] = useState([]);
    const [selectedBranchId, setSelectedBranchId] = useState('');
    const [siteName, setSiteName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const hideOnThisPage = HIDE_ON_PATHS.includes(location.pathname);

    const loadBranches = useCallback(async () => {
        setLoading(true);
        setError('');

        try {
            const token = localStorage.getItem('token')?.replace(/^"(.*)"$/, '$1');
            const response = await axios.get(
                `${process.env.REACT_APP_HIMS_API_URL}/baseadj/branch/master`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data?.status === 'success' && Array.isArray(response.data.data)) {
                setBranches(response.data.data);
            } else {
                setError('Unable to load hospital sites.');
            }
        } catch (requestError) {
            console.error('Error fetching hospital sites:', requestError);
            setError('Failed to load hospital sites. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // Skip site bootstrap on login/registration screens
        if (hideOnThisPage) return;

        const currentSiteId = localStorage.getItem('site_id') || '';
        const currentSiteName =
            localStorage.getItem('hospital_name') ||
            localStorage.getItem('site_code') ||
            '';

        setSelectedBranchId(currentSiteId);
        setSiteName(currentSiteName);

        if (!currentSiteId) {
            setIsOpen(true);
            loadBranches();
        }
    }, [loadBranches, hideOnThisPage]);

    const openSelector = () => {
        setSelectedBranchId(localStorage.getItem('site_id') || '');
        setIsOpen(true);
        if (branches.length === 0) loadBranches();
    };

    const getBranchLabel = (branch) => {
        const code = branch.SiteCode || branch.siteCode || '';
        const name = branch.hospitalName || '';
        const location = branch.location || '';

        if (name && location) return `${name} (${code}) - ${location}`;
        if (name) return `${name} (${code})`;
        return code || `Site ${branch.siteId}`;
    };

    const confirmSite = () => {
        const branch = branches.find(
            (item) => String(item.siteId) === String(selectedBranchId)
        );

        if (!branch) {
            setError('Please select a hospital site.');
            return;
        }

        const previousSiteId = localStorage.getItem('site_id');
        const siteCode = branch.SiteCode || branch.siteCode || '';
        const hospitalName = branch.hospitalName || siteCode;

        localStorage.setItem('site_id', String(branch.siteId));
        localStorage.setItem('site_code', siteCode);
        localStorage.setItem('hospital_name', hospitalName);
        setSiteName(hospitalName);
        setIsOpen(false);

        // Site-dependent screens fetch data on mount, so reload after a real change.
        if (String(previousSiteId) !== String(branch.siteId)) {
            window.location.reload();
        }
    };

    // Not shown on login / registration entry pages
    if (hideOnThisPage) {
        return null;
    }

    return (
        <>
            <div className="sticky top-0 z-50 w-full border-b border-emerald-100 bg-white/95 shadow-sm backdrop-blur">
                <div className="mx-auto flex max-w-[1600px] items-center justify-between gap-2 px-3 py-1.5 sm:px-6">
                    <div className="flex min-w-0 items-center gap-2 text-emerald-800">
                        <MapPin className="h-4 w-4 shrink-0" />
                        <span className="truncate text-xs font-medium sm:text-sm">
                            {siteName || 'No site selected'}
                        </span>
                    </div>
                    <button
                        type="button"
                        onClick={openSelector}
                        className="shrink-0 rounded-lg bg-[#0FA968] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#138F5E] sm:text-sm"
                    >
                        {siteName ? 'Change Site' : 'Select Site'}
                    </button>
                </div>
            </div>

            {isOpen && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 px-4">
                    <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl sm:p-6">
                        <div className="mb-5 flex items-start justify-between gap-3">
                            <div>
                                <h2 className="text-xl font-semibold text-gray-900 sm:text-2xl">
                                    Select Hospital
                                </h2>
                                <p className="mt-1 text-sm text-gray-600">
                                    Select the site for this service.
                                </p>
                            </div>
                            {siteName && (
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="p-1 text-gray-500 hover:text-gray-700"
                                    aria-label="Close site selector"
                                >
                                    <X className="h-5 w-5" />
                                </button>
                            )}
                        </div>

                        <label
                            htmlFor="patient-site-select"
                            className="mb-2 block text-sm font-medium text-gray-700"
                        >
                            Hospital Site
                        </label>
                        <select
                            id="patient-site-select"
                            value={selectedBranchId}
                            onFocus={() => {
                                if (branches.length === 0) loadBranches();
                            }}
                            onChange={(event) => {
                                setSelectedBranchId(event.target.value);
                                setError('');
                            }}
                            disabled={loading}
                            className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 focus:border-[#0FA968] focus:outline-none focus:ring-2 focus:ring-emerald-100"
                        >
                            <option value="">
                                {loading ? 'Loading sites...' : 'Select a site'}
                            </option>
                            {branches.map((branch) => (
                                <option key={branch.siteId} value={branch.siteId}>
                                    {getBranchLabel(branch)}
                                </option>
                            ))}
                        </select>

                        {error && (
                            <div className="mt-3 flex items-start gap-2 text-sm text-red-600">
                                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>{error}</span>
                            </div>
                        )}

                        <div className="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                            {siteName && (
                                <button
                                    type="button"
                                    onClick={() => setIsOpen(false)}
                                    className="rounded-lg border border-gray-300 px-5 py-2.5 text-gray-700 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={confirmSite}
                                disabled={loading || !selectedBranchId}
                                className="rounded-lg bg-[#0FA968] px-5 py-2.5 font-semibold text-white hover:bg-[#138F5E] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Confirm Site
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default PatientSiteSelector;
